import { ConsultationSummary } from '../types';
import Icon from '../../../components/AppIcon';

interface SummaryCardProps {
  summary: ConsultationSummary;
}

const SummaryCard = ({ summary }: SummaryCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'text-success';
      case 'partial':
        return 'text-warning';
      case 'non-compliant':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'CheckCircle2';
      case 'partial':
        return 'AlertTriangle';
      case 'non-compliant':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">
            Resumo da Consulta
          </h2>
          <p className="text-sm text-muted-foreground font-mono">
            ID: {summary.consultationId}
          </p>
        </div>
        <div className={`flex items-center gap-2 ${getStatusColor(summary.complianceStatus)}`}>
          <Icon name={getStatusIcon(summary.complianceStatus)} size={24} color="currentColor" />
          <span className="font-medium capitalize">
            {summary.complianceStatus === 'compliant' && 'Conforme'}
            {summary.complianceStatus === 'partial' && 'Parcialmente Conforme'}
            {summary.complianceStatus === 'non-compliant' && 'Não Conforme'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-muted rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Package" size={16} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground">Total de Produtos</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-mono">
            {summary.totalProducts}
          </p>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Valor Total de Impostos</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-mono">
            {formatCurrency(summary.totalTaxAmount)}
          </p>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Percent" size={16} color="var(--color-accent)" />
            <span className="text-sm text-muted-foreground">Alíquota Média Efetiva</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-mono">
            {summary.averageEffectiveRate.toFixed(2)}%
          </p>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={16} color="var(--color-warning)" />
            <span className="text-sm text-muted-foreground">Tempo de Resposta API</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-mono">
            {summary.apiCallDuration}ms
          </p>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-muted-foreground">Empresa:</span>
            <p className="text-base font-medium text-foreground mt-1">
              {summary.companyName}
            </p>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">CNPJ:</span>
            <p className="text-base font-medium text-foreground font-mono mt-1">
              {summary.cnpj}
            </p>
          </div>
          <div className="md:col-span-2">
            <span className="text-sm text-muted-foreground">Data do Cálculo:</span>
            <p className="text-base font-medium text-foreground mt-1">
              {formatDate(summary.calculatedAt)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;